import { useLayoutEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { MentionText } from '@/features/mentions/MentionText';

interface PostBodyProps {
  text: string;
  /** Lines shown before clamping (spec §9.2: roughly six). */
  clampLines?: number;
  /** Detail pages show the whole thing. */
  expanded?: boolean;
}

/**
 * Post text, clamped with a "Show more" toggle.
 *
 * User text is rendered as **plain text**, never HTML (spec §11) — React's
 * escaping is the whole defence, and there is deliberately no
 * `dangerouslySetInnerHTML` anywhere near it. Line breaks survive through
 * `white-space: pre-wrap` rather than by converting them to markup.
 *
 * The toggle only appears when the text is genuinely clipped, measured after
 * layout rather than guessed from a character count — which would be wrong for
 * any text that wraps unusually.
 */
export function PostBody({ text, clampLines = 6, expanded = false }: PostBodyProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(expanded);
  const [isClamped, setIsClamped] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || expanded) return;

    const check = (): void => setIsClamped(element.scrollHeight > element.clientHeight + 1);
    check();

    // Re-measure on resize: a card that fits on desktop may clamp on mobile.
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, clampLines, expanded, showAll]);

  return (
    <Box>
      <Typography
        ref={ref}
        variant="body1"
        sx={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          ...(showAll
            ? {}
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: clampLines,
                overflow: 'hidden',
              }),
        }}
      >
        <MentionText text={text} />
      </Typography>

      {!expanded && (isClamped || showAll) && (
        <Button
          size="small"
          variant="text"
          onClick={() => setShowAll((current) => !current)}
          sx={{ mt: 0.5, ml: -1 }}
          aria-expanded={showAll}
        >
          {showAll ? t('post.showLess') : t('post.showMore')}
        </Button>
      )}
    </Box>
  );
}
